import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Lock, Refresh, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { Crypter } from '../../../Functions/Security/Crypter';
import { mfaApi, MFA_METHOD, parseEncryptedResponse, normalizeStatusCode, getMethodLabel, logMfaRequest, logMfaSuccess, logMfaError, encryptBody } from './mfaApi';
import { mfaTheme } from './them';
import { useSession } from '../../../Services/Auth/sessionAuth';

interface MfaVerifyPageProps {
  onVerified?: () => void;
}

export const MfaVerifyPage: React.FC<MfaVerifyPageProps> = ({ onVerified }) => {
  const { refreshSession } = useSession();
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [mfaMethod, setMfaMethod] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // Charger la méthode MFA assignée depuis sessionStorage
    const assignedMethod = sessionStorage.getItem('assignedMfaMethod');
    if (assignedMethod) {
      setMfaMethod(Number(assignedMethod));
    }
  }, []);

  // Envoyer automatiquement l'OTP au chargement pour Email/SMS
  useEffect(() => {
    if (mfaMethod && mfaMethod !== MFA_METHOD.none && mfaMethod !== MFA_METHOD.totp) {
      sendChallenge();
    }
  }, [mfaMethod]);

  const sendChallenge = async () => {
    setSendingOtp(true);
    setMessage(null);

    try {
      const codUti = localStorage.getItem('codUti') || sessionStorage.getItem('codUti');
      if (!codUti) {
        setMessage({ type: 'error', text: 'Utilisateur non identifié' });
        setSendingOtp(false);
        return;
      }

      const endpoint = mfaMethod === MFA_METHOD.emailOtp ? mfaApi.emailOtpSend : mfaApi.smsOtpSend;
      const body = { codUti };
      
      logMfaRequest('sendChallenge', endpoint, body);
      const response = await axios.post(endpoint, encryptBody(body), {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      });

      const payload = logMfaSuccess('sendChallenge', response);
      const status = normalizeStatusCode(payload);
      
      if (status === 200) {
        setMessage({ type: 'success', text: 'Code envoyé avec succès' });
        // Démarrer le compte à rebours (5 minutes)
        setCountdown(300);
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setMessage({ type: 'error', text: payload?.Message || 'Erreur lors de l\'envoi du code' });
      }
    } catch (error: any) {
      const payload = logMfaError('sendChallenge', error);
      setMessage({ type: 'error', text: payload?.Message || 'Erreur lors de l\'envoi du code' });
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const codUti = localStorage.getItem('codUti') || sessionStorage.getItem('codUti');
      if (!codUti) {
        setMessage({ type: 'error', text: 'Utilisateur non identifié' });
        setLoading(false);
        return;
      }

      const tempToken = sessionStorage.getItem('tempToken') || sessionStorage.getItem('mfaTempToken') || undefined;
      const body = {
        CodUti: codUti,
        OtpCode: otpCode,
        MethodType: mfaMethod ?? MFA_METHOD.none,
        TemporaryToken: tempToken,
      };

      logMfaRequest('verifyOtp', mfaApi.verifyOtp, { ...body, OtpCode: '******' });
      const response = await axios.post(mfaApi.verifyOtp, encryptBody(body), {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      });

      const payload = logMfaSuccess('verifyOtp', response);
      const status = normalizeStatusCode(payload);

      if (status === 200 || status === 429) {
        // Check if locked out
        if (status === 429 || payload?.Result?.LockoutSeconds !== undefined) {
          const lockoutSeconds = payload?.Result?.LockoutSeconds ?? 60;
          setMessage({ type: 'error', text: `Compte temporairement verrouillé. Veuillez patienter ${lockoutSeconds} secondes.` });
        } else {
          setMessage({ type: 'success', text: 'MFA vérifié avec succès' });
          
          // Mettre à jour la session
          if (await refreshSession()) {
            // Nettoyer sessionStorage
            sessionStorage.removeItem('assignedMfaMethod');
            sessionStorage.removeItem('mfaRequired');
            sessionStorage.removeItem('requiresSetup');
            sessionStorage.removeItem('tempToken');
            sessionStorage.removeItem('mfaTempToken');

            // Rediriger vers la page d'accueil ou appeler le callback
            setTimeout(() => {
              if (onVerified) {
                onVerified();
              } else {
                window.location.replace('/');
              }
            }, 1000);
          } else {
            setMessage({ type: 'error', text: 'Erreur lors de l\'initialisation de la session.' });
          }
        }
      } else {
        setMessage({ type: 'error', text: payload?.Message || 'Code invalide' });
      }
    } catch (error: any) {
      const payload = logMfaError('verifyOtp', error);
      setMessage({ type: 'error', text: payload?.Message || 'Erreur lors de la vérification' });
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
        // backgroundImage: `url(${BgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(18, 75, 122, 0.88) 0%, rgba(7, 16, 24, 0.92) 100%)',
          zIndex: 0,
        },
      }}
    >
      <Card className="login-card light" sx={{ maxWidth: 500, width: '100%', position: 'relative', zIndex: 1 }}>
        <CardContent sx={{ padding: 4 }}>
          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: mfaTheme.primaryBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <Lock sx={{ fontSize: 32, color: mfaTheme.primary }} />
              </Box>
              <Typography className="form-title" variant="h5" fontWeight={600} gutterBottom sx={{ color: mfaTheme.primaryDark }}>
                Vérification MFA
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getMethodLabel()}
              </Typography>
            </Box>

            {/* Message */}
            {message && (
              <Alert severity={message.type} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}

            {/* Instructions */}
            <Alert severity="info">
              {mfaMethod === MFA_METHOD.totp
                ? 'Entrez le code généré par votre application d\'authentification.'
                : 'Un code de vérification vous a été envoyé. Entrez-le ci-dessous.'}
            </Alert>

            {/* Formulaire */}
            <form onSubmit={verifyOtp}>
              <TextField
                fullWidth
                label="Code de vérification"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                placeholder="Entrez le code à 6 chiffres"
                inputProps={{
                  maxLength: 8,
                  inputMode: 'numeric',
                  style: {
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    textAlign: 'center',
                    fontWeight: 600
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock fontSize="small" />
                    </InputAdornment>
                  )
                }}
                disabled={loading}
                autoFocus
              />

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                {(mfaMethod === MFA_METHOD.emailOtp || mfaMethod === MFA_METHOD.smsOtp) && (
                  <Button
                    variant="outlined"
                    onClick={sendChallenge}
                    disabled={sendingOtp || countdown > 0}
                    startIcon={sendingOtp ? <CircularProgress size={20} /> : <Refresh />}
                    sx={{ flex: 1 }}
                  >
                    {sendingOtp ? 'Envoi...' : countdown > 0 ? `Renvoyer (${formatCountdown(countdown)})` : 'Renvoyer'}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !otpCode.trim()}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{ 
                    flex: mfaMethod === MFA_METHOD.totp ? 1 : 2,
                    backgroundColor: mfaTheme.primary,
                    '&:hover': { backgroundColor: mfaTheme.primaryHover }
                  }}
                >
                  {loading ? 'Vérification...' : 'Vérifier'}
                </Button>
              </Stack>
            </form>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => window.location.replace('/Login')}
                startIcon={<ArrowBack />}
              >
                Retour à la connexion
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MfaVerifyPage;